import Plot from 'react-plotly.js'
import { roundToDecimal } from '../../common/utils'
import { ChartSubHeader } from '../ChartSubHeader'
import { CollapsibleSection } from '../CollapsibleSection'
import { useWindowDimensions } from '../../hooks/useWindowDimensions'
import { UserUnitSettings, UnitConversions } from '../../common/constants'
import { DeviceInfo } from '../../common/enums'
import { Viewport } from '../../common/types'
import { getUserUnitSetting } from '../../utils/unitSettings'

const MIN_WIDTH = 700
const MIN_HEIGHT = 700

const theta = [
  'North',
  'NNE',
  'NE',
  'ENE',
  'East',
  'ESE',
  'SE',
  'SSE',
  'South',
  'SSW',
  'SW',
  'WSW',
  'West',
  'WNW',
  'NW',
  'NNW',
]

interface Props {
  device: DeviceInfo
  readings: any
  sensorReadings: any
  sensorPort: string
  chartUnits: string
  viewport: Viewport
  userUnitSettings: UserUnitSettings
  unitConversions: UnitConversions
}

export function WindDirectionChart({
  device,
  readings,
  sensorReadings,
  sensorPort,
  chartUnits,
  viewport,
  userUnitSettings,
  unitConversions,
}: Props) {
  const { width, height } = useWindowDimensions()

  const windDirectionData: any = Object.values(readings).filter((reading: any) => {
    return reading.metadata.productName === 'Wind Direction'
  })[0]

  const windSpeedData: any = Object.values(readings).filter((reading: any) => {
    return reading.metadata.productName === 'Wind Speed'
  })[0]

  const abbreviations = windSpeedData.metadata.units.conversions.abbreviations
  const unitCategoryIdForWindSpeed = windSpeedData.metadata.units.category.id
  const { unitId, precision } = getUserUnitSetting(unitCategoryIdForWindSpeed, userUnitSettings, unitConversions)

  const windDirectionReadings = windDirectionData?.readings
  const windSpeedReadings = windSpeedData?.readings

  let zeroPercent = 0
  let zeroCount = 0
  let dataCount = 0

  let barData: number[][] = Array(16)
    .fill(0)
    .map((_) => Array(8).fill(0))

  const timestamps = Object.keys(windDirectionReadings).filter((timestamp: string) => {
    const ts = Number(timestamp)
    return ts * 1000 >= viewport.minimum && ts * 1000 <= viewport.maximum
  })

  timestamps.map((timestamp: string) => {
    const ts = Number(timestamp)

    const windDirection = windDirectionReadings[ts]
    const windSpeed = windSpeedReadings[ts]

    if (windSpeed > 0) {
      const i = getWindDirectionBin(windDirection)
      const j = getWindSpeedBin(windSpeed, unitId)
      barData[i][j]++
    } else {
      zeroCount++
    }

    dataCount++

    return null
  })

  let windRoseData: Record<string, any>[] = []

  if (dataCount) {
    zeroPercent = roundToDecimal((100 * zeroCount) / dataCount / 16, precision)

    barData = barData.map((row) => {
      return row.map((value) => roundToDecimal((100 * value) / dataCount, precision))
    })

    windRoseData.push({
      theta,
      hoverInfo: 'r+theta+text',
      type: 'barpolar',
      r: Array(16).fill(zeroPercent),
      name: getWindSpeedName(-1, unitId),
      marker: getWindSpeedColor(-1),
      text: getWindSpeedHoverText(-1, unitId),
    })

    for (let j = 0; j < 8; j++) {
      windRoseData.push({
        theta,
        hoverInfo: 'r+theta+text',
        type: 'barpolar',
        r: barData.map((row) => row[j]),
        name: getWindSpeedName(j, unitId),
        marker: getWindSpeedColor(j),
        text: getWindSpeedHoverText(j, unitId),
      })
    }
  } else {
    windRoseData.push({
      theta,
      type: 'barpolar',
      r: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      name: '0 mph (Calm)',
      marker: { color: 'rgb(225,225,225)' },
    })
  }

  const windoRoseLayout: Partial<Plotly.Layout> = {
    title: 'Wind Speed Distribution',
    font: { size: 14 },

    width: (width < MIN_WIDTH ? MIN_WIDTH : width) * 0.75,
    height: (height < MIN_HEIGHT ? MIN_HEIGHT : height) * 0.75,
    legend: {
      font: { size: 14 },
      traceorder: 'reversed',
    },
    polar: {
      // barmode: 'stacked',
      // bargap: 0.1,
      radialaxis: { ticksuffix: '%', angle: 45, dtick: 4 },
      angularaxis: { direction: 'clockwise' },
    },
  }

  function getLastReadingWindSpeed() {
    const timestamps = Object.keys(windSpeedReadings)
    const lastTimestamp = timestamps[timestamps.length - 1]
    return windSpeedReadings[lastTimestamp]
  }

  function getLastReadingWindDirection() {
    const timestamps = Object.keys(windDirectionReadings)
    const lastTimestamp = timestamps[timestamps.length - 1]
    return windDirectionReadings[lastTimestamp]
  }

  function getLastReadingTimestamp(): string {
    return sensorReadings?.metadata?.timestamps?.end
      ? new Date(sensorReadings.metadata.timestamps.end * 1000).toLocaleString()
      : 'No readings'
  }

  const data: string[][] = [
    [
      `Port ${sensorPort}: ${windDirectionData.metadata.productName}`,
      `${getLastReadingTimestamp()}`,
      `${getLastReadingWindSpeed()} ${abbreviations[unitId]}`,
      `${theta[getWindDirectionBin(getLastReadingWindDirection())]} (${Math.floor(getLastReadingWindDirection())}°)`,
    ],
  ]

  return (
    <CollapsibleSection title="Wind Direction">
      <ChartSubHeader data={data} />
      <Plot className="mx-auto" data={windRoseData} layout={windoRoseLayout} />
    </CollapsibleSection>
  )
}

function getWindSpeedName(index: number, unitId: number) {
  let name = undefined

  if (unitId === 1) {
    // feet per second
    name =
      [
        '1.0 - 5.8 f/s (Light)',
        '5.8 - 11.7 f/s (Light)',
        '11.7 - 19.0 f/s (Gentle)',
        '19.0 - 27.8 f/s (Moderate)',
        '27.8 - 36.6 f/s (Fresh)',
        '36.6 - 46.9 f/s (Strong)',
        '46.9 - 57.2 f/s (Strong)',
        '57.2+ f/s (Gale)',
      ][index] || '0.0 - 1.0 f/s (Calm)'
  }

  if (unitId === 2) {
    // mph
    name =
      [
        '1 - 4 mph (Light)',
        '4 - 8 mph (Light)',
        '8 - 13 mph (Gentle)',
        '13 - 19 mph (Moderate)',
        '19 - 25 mph (Fresh)',
        '25 - 32 mph (Strong)',
        '32 - 39 mph (Strong)',
        '39+ mph (Gale)',
      ][index] || '0 - 1 mph (Calm)'
  }

  if (unitId === 65) {
    // meters per second
    name =
      [
        '0.5 - 1.6 m/s (Light)',
        '1.6 - 3.3 m/s (Light)',
        '3.3 - 5.5 m/s (Gentle)',
        '5.5 - 8.0 m/s (Moderate)',
        '8.0 - 10.8 m/s (Fresh)',
        '10.8 - 13.8 m/s (Strong)',
        '13.8 - 17.2 m/s (Strong)',
        '17.2+ m/s (Gale)',
      ][index] || '0.0 - 0.5 m/s (Calm)'
  }

  if (unitId === 66) {
    // kph
    name =
      [
        '1 - 6 kph (Light)',
        '6 - 12 kph (Light)',
        '12 - 20 kph (Gentle)',
        '20 - 29 kph (Moderate)',
        '29 - 39 kph (Fresh)',
        '39 - 50 kph (Strong)',
        '50 - 62 kph (Strong)',
        '62+ kph (Gale)',
      ][index] || '0 - 1 kph (Calm)'
  }

  return name
}

/************************************************************************************************
 *
 ************************************************************************************************/
function getWindSpeedHoverText(index: number, unitId: number) {
  let result = undefined

  if (unitId === 1) {
    // feet per second
    result =
      [
        '1.0 - 5.8 f/s',
        '5.8 - 11.7 f/s',
        '11.7 - 19.0 f/s',
        '19.0 - 27.8 f/s',
        '27.8 - 36.6 f/s',
        '36.6 - 46.9 f/s',
        '46.9 - 57.2 f/s',
        '57.2+ f/s',
      ][index] || '0.0 - 1.0 f/s'
  }

  if (unitId === 2) {
    // mph
    result =
      ['1 - 4 mph', '4 - 8 mph', '8 - 13 mph', '13 - 19 mph', '19 - 25 mph', '25 - 32 mph', '32 - 39 mph', '39+ mph'][
        index
      ] || '0 - 1 mph'
  }

  if (unitId === 65) {
    // meters per second
    result =
      [
        '0.5 - 1.6 m/s',
        '1.6 - 3.3 m/s',
        '3.3 - 5.5 m/s',
        '5.5 - 8.0 m/s',
        '8.0 - 10.8 m/s',
        '10.8 - 13.8 m/s',
        '13.8 - 17.2 m/s',
        '17.2+ m/s',
      ][index] || '0.0 - 0.5 m/s'
  }

  if (unitId === 66) {
    // kph
    result =
      ['1 - 6 kph', '6 - 12 kph', '12 - 20 kph', '20 - 29 kph', '29 - 39 kph', '39 - 50 kph', '50 - 62 kph', '62+ kph'][
        index
      ] || '0 - 1 kph'
  }

  return result
}

function getWindSpeedColor(index: number) {
  const color =
    [
      'rgb(205,255,255)',
      'rgb(153,255,153)',
      'rgb(153,255,0)',
      'rgb(204,255,0)',
      'rgb(255,255,0)',
      'rgb(255,204,0)',
      'rgb(255,153,0)',
      'rgb(255,0,0)',
    ][index] || 'rgb(225,225,225)'

  return { color }
}

function getWindSpeedBin(speed: number, unitId: number) {
  let lookup: number[] = []

  if (unitId === 1) {
    lookup = [
      1.78816, // 5.8 fps
      3.57632, // 11.7 fps
      5.81152, // 19.0 fps
      8.49376, // 27.8 fps
      11.176, // 36.6 fps
      14.30528, // 46.9 fps
      17.43456, // 57.2 fps
    ]
  }

  if (unitId === 2) {
    lookup = [
      1.78816, // 4 mph
      3.57632, // 8 mph
      5.81152, // 13 mph
      8.49376, // 19 mph
      11.176, // 25 mph
      14.30528, // 32 mph
      17.43456, // 37 mph
    ]
  }

  if (unitId === 65) {
    lookup = [
      1.66666, // 1.6 mps
      3.33333, // 3.3 mps
      5.55555, // 5.5 mps
      8.05555, // 8.0 mps
      10.83333, // 10.8 mps
      13.88888, // 13.8 mps
      17.22222, // 17.2 mps
    ]
  }

  if (unitId === 66) {
    lookup = [
      1.66666, // 6 kph
      3.33333, // 12 kph
      5.55555, // 20 kph
      8.05555, // 29 kph
      10.83333, // 39 kph
      13.88888, // 50 kph
      17.22222, // 62 kph
    ]
  }

  if (lookup.length) {
    for (const [index, value] of Object.entries(lookup)) {
      if (speed < value) {
        return Number(index)
      }
    }
  }

  return 0
}

function getWindDirectionBin(degrees: number) {
  const binSize = 360 / 16
  const binIndex = Math.floor(degrees / binSize + 0.5) % 16
  return binIndex === 0 ? 15 : binIndex
}
